import React, { useState, useEffect } from "react";
import { supabase } from "../supabaseClient";
import { useTranslation } from "react-i18next";

const ResetPassword = () => {
    const { t } = useTranslation();
    const [password, setPassword] = useState("");
    const [message, setMessage] = useState("");
    const [messageType, setMessageType] = useState("");
    const [loading, setLoading] = useState(false);
    const [ready, setReady] = useState(false);

    // Attend que Supabase ait établi la session de récupération à partir du jeton
    // présent dans l'URL. Le traitement du jeton est ASYNCHRONE → on écoute
    // onAuthStateChange plutôt que d'appeler getSession() une seule fois trop tôt.
    useEffect(() => {
        let settled = false;
        const markReady = () => {
            if (settled) return;
            settled = true;
            setReady(true);
            setMessage("");
            setMessageType("");
            // Nettoie l'URL (hash ou ?code)
            window.history.replaceState(null, "", window.location.pathname);
        };

        // 1) Événement de récupération / connexion : le client vient de traiter le jeton
        const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
            if (session) markReady();
        });

        // 2) Tentative immédiate (+ échange PKCE si un ?code est présent)
        (async () => {
            try {
                const url = new URL(window.location.href);
                if (url.searchParams.get("code")) {
                    await supabase.auth.exchangeCodeForSession(window.location.href).catch(() => { });
                }
            } catch { /* noop */ }
            const { data } = await supabase.auth.getSession();
            if (data?.session) markReady();
        })();

        // 3) Filet : au bout de 3s sans session → lien invalide/expiré
        const timer = setTimeout(async () => {
            const { data } = await supabase.auth.getSession();
            if (!data?.session && !settled) {
                setMessageType("error");
                setMessage(t('resetPassword.errors.invalidLink'));
            }
        }, 3000);

        return () => {
            sub.subscription.unsubscribe();
            clearTimeout(timer);
        };
    }, [t]);

    // Traduit les messages d'erreur Supabase (anglais) les plus courants
    const frError = (msg = "") => {
        if (/different from the old password/i.test(msg)) return t('resetPassword.errors.samePassword');
        if (/should be at least|password should be|at least 6/i.test(msg)) return t('resetPassword.errors.tooShort');
        if (/session missing|invalid|expired|not authenticated/i.test(msg)) return t('resetPassword.errors.invalidLink');
        return `${t('common.error') || "Erreur"} : ${msg}`;
    };

    const handleReset = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage("");
        setMessageType("");

        const { error } = await supabase.auth.updateUser({ password });

        if (error) {
            console.error("Erreur reset password :", error.message);
            setMessageType("error");
            setMessage(frError(error.message));
        } else {
            setMessageType("success");
            setMessage(t('resetPassword.success.passwordChanged'));
            await supabase.auth.signOut();
            setTimeout(() => { window.location.href = "/"; }, 2000);
        }

        setLoading(false);
    };

    if (!ready && !message)
        return (
            <p style={{ textAlign: "center", marginTop: "50px" }}>
                {t('resetPassword.validating')}
            </p>
        );

    return (
        <div
            style={{
                maxWidth: 400,
                margin: "80px auto",
                padding: "30px",
                borderRadius: "12px",
                boxShadow: "0 0 12px rgba(0,0,0,0.1)",
                background: "white",
                textAlign: "center",
                fontFamily: "Arial, sans-serif",
            }}
        >
            <h2 style={{ color: "var(--primary-blue)", marginBottom: 10 }}>
                {t('resetPassword.title')}
            </h2>
            <p style={{ color: "#555", fontSize: "0.95rem", marginBottom: 20 }}>
                {t('resetPassword.instructions')}
            </p>

            <form onSubmit={handleReset}>
                <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('resetPassword.placeholder')}
                    required
                    style={{
                        width: "100%",
                        padding: "10px",
                        borderRadius: "6px",
                        border: "1px solid #ccc",
                        marginBottom: "15px",
                    }}
                />

                <button
                    type="submit"
                    disabled={loading}
                    style={{
                        width: "100%",
                        padding: "10px",
                        background: loading ? "#a0c8ff" : "var(--primary-blue)",
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        cursor: loading ? "not-allowed" : "pointer",
                        fontWeight: "bold",
                    }}
                >
                    {loading ? t('resetPassword.buttons.submitting') : t('resetPassword.buttons.submit')}
                </button>
            </form>

            {message && (
                <p
                    style={{
                        marginTop: 20,
                        fontSize: "0.95rem",
                        color: messageType === "error" ? "red" : "green",
                        lineHeight: 1.4,
                    }}
                >
                    {message}
                </p>
            )}

            <button
                type="button"
                onClick={() => (window.location.href = "/")}
                style={{
                    marginTop: 25,
                    background: "none",
                    border: "none",
                    color: "var(--primary-blue)",
                    textDecoration: "underline",
                    cursor: "pointer",
                    fontSize: "0.9rem",
                }}
            >
                {t('resetPassword.buttons.backToLogin')}
            </button>
        </div>
    );
};

export default ResetPassword;
